/**
 * 提示语模板管理Hook
 * 职责: 处理模板加载、保存等业务逻辑
 */
import { useState, useEffect } from 'react';
import { Form, App } from 'antd';
import aiAnalysisService from '../../../services/aiAnalysisService';
import type { PromptTemplate, TemplateType } from '../../../types/ai-analysis-types';
import { DEFAULT_PROMPTS } from '../constants/promptDefaults';
import { DEFAULT_VARIABLES } from '../constants/promptVariables';

export const usePromptTemplate = (selectedType: TemplateType, form: ReturnType<typeof Form.useForm>[0]) => {
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [currentTemplate, setCurrentTemplate] = useState<PromptTemplate | null>(null);
    const { message: messageApi } = App.useApp();

    const loadTemplate = async (type: TemplateType) => {
        setLoading(true);
        try {
            try {
                const template = await aiAnalysisService.getPromptTemplateByType(type);
                setCurrentTemplate(template);

                // 如果后端返回的 variables 为空对象或 null，使用默认变量
                const variables = (template.variables && Object.keys(template.variables).length > 0)
                    ? template.variables
                    : DEFAULT_VARIABLES[type] || {};

                form.setFieldsValue({
                    content: template.content,
                    is_active: template.is_active,
                    variables: JSON.stringify(variables, null, 2)
                });
            } catch (error: any) {
                if (error.response && error.response.status === 404) {
                    setCurrentTemplate(null);
                    form.setFieldsValue({
                        content: DEFAULT_PROMPTS[type],
                        variables: JSON.stringify(DEFAULT_VARIABLES[type] || {}, null, 2),
                        is_active: true
                    });
                } else {
                    throw error;
                }
            }
        } catch (error) {
            console.error('加载模板失败:', error);
            messageApi.error('加载模板失败');
        } finally {
            setLoading(false);
        }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        loadTemplate(selectedType);
    }, [selectedType]);

    const handleSave = async (values: any) => {
        setSaving(true);
        try {
            let variables = {};
            try {
                variables = JSON.parse(values.variables);
            } catch (e) {
                messageApi.error('变量格式必须是有效的 JSON');
                setSaving(false);
                return;
            }

            const templateData = {
                template_type: selectedType,
                content: values.content,
                variables: variables,
                is_active: values.is_active
            };

            if (currentTemplate && currentTemplate.id) {
                const updated = await aiAnalysisService.updatePromptTemplate(currentTemplate.id, templateData);
                setCurrentTemplate(updated);
                messageApi.success('保存成功');
            } else {
                const created = await aiAnalysisService.createPromptTemplate(templateData as PromptTemplate);
                setCurrentTemplate(created);
                messageApi.success('创建成功');
            }
        } catch (error) {
            console.error('保存模板失败:', error);
            messageApi.error('保存失败');
        } finally {
            setSaving(false);
        }
    };

    const handleResetToDefault = () => {
        form.setFieldsValue({
            content: DEFAULT_PROMPTS[selectedType],
            variables: JSON.stringify(DEFAULT_VARIABLES[selectedType] || {}, null, 2)
        });
        messageApi.info('已恢复为系统默认提示语（需点击保存生效）');
    };

    return {
        loading,
        saving,
        currentTemplate,
        handleSave,
        handleResetToDefault
    };
};
